/* eslint-disable functional/immutable-data */
/* eslint-disable max-lines-per-function */
/* eslint-disable functional/prefer-readonly-type */
import React, { useEffect, useCallback, useMemo } from 'react'
import { LatLng } from 'leaflet'
import { State, useHookstate } from '@hookstate/core'
import { debounce } from 'lodash'
import {
  getCityIdByName,
  getDistrictIdByName,
  getPlaceDetails,
  LocationPrediction as BaseLocationPrediction,
  getAutoCompleteOptionsWithInterestPoint,
} from '~/utils/location'
import { useMarkerState } from '~/features/Map/stores/MapState'
import { FetchAutoComplete } from '../FetchAutoComplete'
import { LocationOn } from '@mui/icons-material'
import { Typography } from '@mui/material'
import { useLang } from '~/hooks/useLang'

type LocationPrediction = BaseLocationPrediction & {
  from_interest_point?: boolean
  city_id?: number | string
  district_id?: number | string
  latitude?: number
  longitude?: number
  description?: string
  main?: string
}

export type Type = 'dispatch' | 'entry' | 'occurrence'

export type LocationState = {
  readonly id: State<number | string | null>
  readonly latitude: State<number | null>
  readonly longitude: State<number | null>
  readonly cityId: State<number | string | null>
  readonly districtId?: State<number | string | null>
  readonly locationValue: State<string | null>
  readonly type: Type
}

interface LocalityTextFieldProps {
  readonly readonly?: boolean
  readonly locationState: LocationState
  readonly isInvalidRequest?: boolean
  readonly isValid: boolean
  readonly reference?: any
}

interface IAddress {
  readonly city?: string
  readonly secondary?: string
  readonly town?: string
  readonly latitude: number
  readonly longitude: number
  readonly district?: string
}

const cityCache = new Map<string, number | string>()
const districtCache = new Map<string, number | string>()

export function LocalityTextField({
  readonly = false,
  locationState,
  isInvalidRequest,
  isValid,
  reference,
}: LocalityTextFieldProps) {
  const { id, latitude, longitude, cityId, locationValue, type, districtId } =
    locationState
  const { boundCenterActual, createMarker, forceViewMap, updateMarker } = useMarkerState()
  const showError = useHookstate(true)
  const { translate } = useLang()

  const setDistrictState = async (address: IAddress) => {
    const district = address.district
    if (district && districtId) {
      if (districtCache.has(district)) {
        districtId.set(districtCache.get(district) ?? null)
      } else {
        const id = await getDistrictIdByName(district)
        districtCache.set(district, id)
        districtId.set(id)
      }
    }
  }

  const setCityStates = async (address: IAddress) => {
    const city = address.city || address.town || null
    if (city) {
      if (cityCache.has(city)) {
        cityId.set(cityCache.get(city) ?? null)
      } else {
        const id = await getCityIdByName(city)
        cityCache.set(city, id)
        cityId.set(id)
      }
    }
  }


  const getAutoCompleteFunction = (newInput: string) => {
    const lat: number | null = boundCenterActual().lat.get()
      ? boundCenterActual().lat.get()
      : null
    const lng: number | null = boundCenterActual().lng.get()
      ? boundCenterActual().lng.get()
      : null

    return getAutoCompleteOptionsWithInterestPoint(newInput, {
      latLng: lat && lng ? new LatLng(lat, lng) : undefined,
    })
  }

  useEffect(() => {
    const currentValue = locationValue.get()
    showError.set(currentValue?.length === 0)
  }, [locationValue.get(), isInvalidRequest])

  const orderBy = (options: any[]) => {
    return options.map((option) => ({
      priority: option.from_interest_point
        ? translate('Pontos de Interesse')
        : translate('Endereços'),
      ...option,
    }))
  }

  const setLocationFunc = async (
    option: LocationPrediction
  ): Promise<{ readonly ended: boolean; readonly value: string }> => {
    const idOrNull = id.get()
    if (!idOrNull || !option) {
      return { ended: false, value: '' }
    }

    try {
      let locationDetail: IAddress | null = null
      let finalLatitude: number | null = null
      let finalLongitude: number | null = null

      if (option.from_interest_point) {
        reference.set(option.main ?? null)
        finalLatitude = option.latitude ?? null
        finalLongitude = option.longitude ?? null
        locationDetail = {
          latitude: finalLatitude,
          longitude: finalLongitude,
          city: undefined,
          district: undefined,
        }
        if (option.city_id) cityId.set(option.city_id)
        if (option.district_id && districtId) districtId.set(option.district_id)
      } else {
        locationDetail = {
          latitude: option.latitude ?? null,
          longitude: option.longitude ?? null,
          city: (option as any).main ?? undefined,
          district: (option as any).district_id ?? undefined,
        }
      }


      if (option.placeId) {
        const placeDetail: IAddress | null = await getPlaceDetails(option.placeId)
        if (placeDetail) {
          locationDetail = placeDetail
          finalLatitude = placeDetail.latitude
          finalLongitude = placeDetail.longitude
          await Promise.all([
            setCityStates(placeDetail),
            placeDetail.district && districtId
              ? setDistrictState(placeDetail)
              : Promise.resolve(),
          ])
        }
      } else if (locationDetail?.city || locationDetail?.district) {
        finalLatitude = locationDetail.latitude
        finalLongitude = locationDetail.longitude
        await Promise.all([
          locationDetail.city
            ? setCityStates(locationDetail)
            : Promise.resolve(),
          locationDetail.district
            ? setDistrictState(locationDetail)
            : Promise.resolve(),
        ])
      }

      if (finalLatitude != null && finalLongitude != null) {
        latitude.set(finalLatitude)
        longitude.set(finalLongitude)
        const position = new LatLng(finalLatitude, finalLongitude)
        const currentPosition = forceViewMap().position.get()

        if (
          currentPosition?.lat !== finalLatitude ||
          currentPosition?.lng !== finalLongitude
        ) {
          const params = {
            id: Number(idOrNull),
            isCurrent: true,
            position,
            location: option?.description ?? undefined,
            status: 1,
          }

          if (type === 'entry') {
            createMarker({
              markerType: type,
              markerIdentifier: params.id,
              ...params,
            })
          }

          if (type === 'dispatch' || type === 'occurrence') {
            updateMarker({
              markerType: type,
              markerIdentifier: params.id,
              attribute: 'location',
              value: params.location,
              ...params,
            })

            updateMarker({
              markerType: type,
              markerIdentifier: params.id,
              attribute: 'position',
              value: params.position,
              ...params,
            })
          }


          forceViewMap().position.set(params.position)
        }
      }

      return {
        ended: true,
        value: option.description ?? option.main ?? '',
      }
    } catch (err) {
      console.error('Erro em setLocationFunc:', err)
      return {
        ended: false,
        value: option?.description ?? option.main ?? '',
      }
    }
  }

  if (!readonly) {
    return (
      <FetchAutoComplete<LocationPrediction>
        fetchFunc={getAutoCompleteFunction}
        setFunc={setLocationFunc}
        noOptionsString={'No Locations, yet...'}
        optionKey={'main'}
        stateValue={locationValue}
        startIcon
        startIconElement={<LocationOn fontSize="small" />}
        autoCompleteProps={{
          getOptionLabel: (option) => option?.description ?? option.main,
          placeholder: 'Location',
          size: 'small',
          errorMsg: false,
          filterOptions: (original) => orderBy(original),
          groupBy: (option) => option.priority,
          renderGroup: (params) => (
            <div key={params.key}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  px: 1,
                  py: 0.5,
                }}
              >
                {params.group}
              </Typography>
              {params.children}
            </div>
          ),
        }}
        isValid={isValid}
        error={isInvalidRequest && showError.get()}
      />
    )
  } else {
    return (
      <Typography>
        {translate('Location')}
        :
        {' '}
        {locationValue.get() ?? translate('No information')}
      </Typography>
    )
  }
}
